import '@Pages/GeoMMM/Results/InvestmentPerformance.scss';
import * as R from 'ramda';
import { Fragment, useCallback, useEffect, useMemo, useState, FC } from 'react';
import { abbreviateNumber, formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { AttributionOverTime, ROI } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import { currencyFormatter, snapShotDataSort } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { DatePicker } from '@Components/DatePicker';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { ChartContainer } from '@Components/ChartContainer';
import { WidgetContainer } from '@Components/WidgetContainer';
import { StackedAreaChart } from '@Pages/MMM/ModelResults/StackedAreaChart';
import {
  formattedAreaData,
  formattedSnapShotData,
  OUTCOME_FORMATTER_TYPE,
  OUTCOME_TYPE_STRING
} from '@Pages/GeoMMM/DataFormatters';
import { OutcomeAndResponseChart } from '@Pages/GeoMMM/Results/Components/OutcomeAndResponseChart';
import { SnapshotChartWithCI } from '@Pages/GeoMMM/Results/Components/SnapshotChartWithCI';
import { Design, Placement, Size } from '@Components/designConstants';
import { Neutral400 } from '@src/utils/colors';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { dateToMddyy, DateGroupingOption, getDefaultDates } from '../../../utils/date-utils';

const HARD_CODED_COLOR = Neutral400;
const HARD_CODED_COLOR_NAME = 'Baseline';

const getYAxisTickFormatter = (
  investment: string,
  outcomeVariable: 'revenue' | 'kpiVolume'
): ((value: any) => string) | undefined => {
  if (investment === 'roas') {
    return undefined;
  }
  if (investment === 'costPerAcquisition') {
    return val => currencyFormatter.format(val, 0);
  }
  return val => OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0);
};

const DEFAULT_SORT_SELECTIONS = {
  spendAndEffectShare: 'Spend: High to low',
  kpiVolume: 'Highest to lowest',
  revenue: 'Highest to lowest',
  costPerAcquisition: 'Highest to lowest',
  roas: 'Highest to lowest'
};

const WIDGET_SUBHEADER = {
  kpiVolume: (
    <>
      Attribution of kpi volume to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  revenue: (
    <>
      Attribution of revenue to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  costPerAcquisition: (
    <>
      Attribution of cost-pers to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) /
        ((level of media investment at that point in time) x (model parameter estimates for that
        media input)).
      </InfoTooltip>
    </>
  ),
  roas: (
    <>
      Attribution of ROAS to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as ((level of media investment at that point in time) x
        (model parameter estimates for that media input)) / (level of media investment at that point
        in time).
      </InfoTooltip>
    </>
  ),
  spendAndEffectShare: (
    <>
      Attribution of spend and effect share to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Effect share calculated for each point in time as ((level of media investment at that point
        in time) x (model parameter estimates for that media input)) / (total KPI volume).
      </InfoTooltip>
    </>
  )
};

const DEFAULT_SORT_OPTIONS = [
  { value: 'Alphabetical' },
  { value: 'Highest to lowest' },
  { value: 'Lowest to highest' }
];

export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: 'Week', label: 'Weekly' },
  { value: 'Month', label: 'Monthly' },
  { value: 'Quarter', label: 'Quarterly' }
];

const SNAPCHART_TITLE = {
  spendAndEffectShare: 'Spend and Effect Share',
  kpiVolume: 'KPI Volume Decomposition Share',
  revenue: 'Revenue Decomposition Share',
  costPerAcquisition: 'Average CPA',
  roas: 'Average ROAS'
};

interface InvestmentPerformanceProps {
  groupBy: string;
  outcomeVariable: 'revenue' | 'kpiVolume';
  attributionOverTimeRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
}

export const InvestmentPerformance: FC<InvestmentPerformanceProps> = ({
  groupBy,
  outcomeVariable,
  attributionOverTimeRows,
  channelBreakoutMap
}) => {
  const [investment, setInvestment] = useState<string>(outcomeVariable);
  const [investmentOptions, setInvestmentOptions] = useState<
    { label: string; value: string; title: string }[]
  >([]);
  const [snapChartSortValue, setSnapChartSortValue] = useState('Highest to lowest');
  const [toggleLabel, setToggleLabel] = useState(false);
  const [absoluteOrRelative, setAbsoluteOrRelative] = useState('Absolute');
  const [dateGrouping, setDateGrouping] = useState('Week');
  const [dates, setDates] = useState({ start: '', end: '' });
  const [covariates, setCovariates] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const [areaChartData, setAreaChartData] = useState<any>({});
  const [areaChartData2, setAreaChartData2] = useState<any>({});
  const [snapChartData, setSnapChartData] = useState<any>([]);

  const SPEND_AND_EFFECT_SORT_OPTIONS = [
    { value: 'Alphabetical' },
    { value: 'Spend to effect ratio' },
    { value: 'Spend: High to low' },
    { value: 'Spend: Low to high' },
    { value: `${OUTCOME_TYPE_STRING[outcomeVariable]}: High to low` },
    { value: `${OUTCOME_TYPE_STRING[outcomeVariable]}: Low to high` }
  ] as const;

  useEffect(() => {
    if (outcomeVariable) {
      setInvestment(outcomeVariable === 'revenue' ? 'revenue' : 'kpiVolume');

      const isRevenue = outcomeVariable === 'revenue';
      setInvestmentOptions([
        {
          label: OUTCOME_TYPE_STRING[outcomeVariable],
          value: outcomeVariable,
          title: `${OUTCOME_TYPE_STRING[outcomeVariable]} Decomposition`
        },
        {
          label: isRevenue ? 'ROAS' : 'Cost Per Acquisition',
          value: isRevenue ? 'roas' : 'costPerAcquisition',
          title: isRevenue ? 'ROAS' : 'Cost Per Acquisition'
        },
        {
          label: 'Spend and Effect Share',
          value: 'spendAndEffectShare',
          title: 'Spend and Effect'
        }
      ]);
    }
  }, [outcomeVariable]);

  const { data } = useMemo(() => {
    if (R.isEmpty(attributionOverTimeRows)) {
      return { roiData: [], attributionOverTimeData: [] };
    }

    // ATTRIBUTION OVER TIME DATA
    const attributionOverTimeDataMap: Record<string, AttributionOverTime> = {};
    attributionOverTimeRows.forEach(row => {
      const {
        channel: breakout,
        time: date,
        metric,
        incremental_outcome: incrementalOutcome,
        spend
      } = row;
      const key = `${breakout.toLowerCase()}_${date}`;
      if (breakout === 'Baseline') {
        attributionOverTimeDataMap[key] = {
          date,
          name: breakout,
          channel: 'baseline',
          platform: null,
          tactic: null,
          brand_nonbrand: null,
          incrementalOutcome,
          incrementalOutcomeLower: incrementalOutcome,
          incrementalOutcomeUpper: incrementalOutcome,
          spend
        };
        return;
      }
      if (channelBreakoutMap[breakout.toLowerCase()]) {
        if (!attributionOverTimeDataMap[key]) {
          const { channel, platform, tactic, brand_nonbrand } =
            channelBreakoutMap[breakout.toLowerCase()];

          attributionOverTimeDataMap[key] = {
            date,
            name: breakout,
            channel: channel?.replace(/[ -]/g, '_') ?? null,
            platform: platform?.replace(/[ -]/g, '_') ?? null,
            tactic: tactic?.replace(/[ -]/g, '_') ?? null,
            brand_nonbrand: brand_nonbrand?.replace(/[ -]/g, '_') ?? null,
            incrementalOutcome: 0,
            incrementalOutcomeLower: 0,
            incrementalOutcomeUpper: 0,
            spend
          };
        }

        if (metric === 'mean') {
          attributionOverTimeDataMap[key].incrementalOutcome = incrementalOutcome;
        } else if (metric === 'ci_lo') {
          attributionOverTimeDataMap[key].incrementalOutcomeLower = incrementalOutcome;
        } else if (metric === 'ci_hi') {
          attributionOverTimeDataMap[key].incrementalOutcomeUpper = incrementalOutcome;
        }
      }
    });

    return {
      data: R.values(attributionOverTimeDataMap)
    };
  }, [attributionOverTimeRows, channelBreakoutMap]);

  useEffect(() => {
    if (data && !R.isEmpty(data) && (dates.start === '' || dates.end === '')) {
      setDates(getDefaultDates(data));
      return;
    }

    if (data && !R.isEmpty(data) && dates.start !== '' && dates.end !== '') {
      let dateRangeFilteredData = R.filter(item => {
        const itemDate = new Date((item as any).date);
        return itemDate >= new Date(dates.start) && itemDate <= new Date(dates.end);
      })(data);

      dateRangeFilteredData = !covariates
        ? R.filter(R.compose(R.not, R.propEq('name', 'Baseline')))(dateRangeFilteredData)
        : dateRangeFilteredData;

      let chartData;
      switch (investment) {
        case 'kpiVolume':
        case 'revenue':
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: 'incrementalOutcome',
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === 'Relative'
          });
          break;
        case 'costPerAcquisition':
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: 'spend',
            groupBy,
            dateGrouping,
            data2Key: 'incrementalOutcome'
          });
          break;
        case 'roas':
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: 'incrementalOutcome',
            groupBy,
            dateGrouping,
            data2Key: 'spend'
          });
          break;
        default: {
          // spend and effect
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: 'spend',
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === 'Relative'
          });
          const chartData2 = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: 'incrementalOutcome',
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === 'Relative'
          });

          setAreaChartData2(chartData2);
          break;
        }
      }

      setAreaChartData(chartData);

      // Iterate through data and use a map to group by date by aggregating the different values in the data
      const snapShotDataMap: Record<string, ROI> = {};
      dateRangeFilteredData.forEach(row => {
        const {
          name,
          channel,
          platform,
          tactic,
          brand_nonbrand,
          incrementalOutcome,
          incrementalOutcomeLower,
          incrementalOutcomeUpper,
          spend
        } = row;
        if (!snapShotDataMap[name]) {
          snapShotDataMap[name] = {
            name,
            channel,
            platform,
            tactic,
            brand_nonbrand,
            incrementalOutcome: 0,
            incrementalOutcomeLower: 0,
            incrementalOutcomeUpper: 0,
            spend: 0
          };
        }

        snapShotDataMap[name].incrementalOutcome += incrementalOutcome;
        snapShotDataMap[name].incrementalOutcomeLower += incrementalOutcomeLower;
        snapShotDataMap[name].incrementalOutcomeUpper += incrementalOutcomeUpper;
        snapShotDataMap[name].spend += spend;
      });

      const snapShotData = formattedSnapShotData({
        data: R.values(snapShotDataMap),
        groupBy,
        conversionPrettyName: OUTCOME_TYPE_STRING[outcomeVariable],
        type: investment,
        outcomeVariable
      });

      snapShotData.barData = snapShotDataSort(snapShotData.barData, snapChartSortValue);
      setSnapChartData(snapShotData);
    }
  }, [
    absoluteOrRelative,
    attributionOverTimeRows,
    covariates,
    dateGrouping,
    dates,
    groupBy,
    investment,
    snapChartSortValue,
    data,
    outcomeVariable,
    logScale
  ]);

  const revenueAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === 'Absolute') {
        return val === 0
          ? OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0)
          : OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 2);
      }
      return val === 0 ? `${val.toFixed(0)}%` : `${val.toFixed(2)}%`;
    },
    [absoluteOrRelative, outcomeVariable]
  );

  const spendAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === 'Absolute') {
        return val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1);
      }
      return absoluteOrRelative === 'Relative' ? `${val.toFixed(0)}%` : '';
    },
    [absoluteOrRelative]
  );

  const buildSnapshotHeader = useCallback(() => {
    switch (investment) {
      case 'kpiVolume':
      case 'revenue':
        return `Total ${OUTCOME_TYPE_STRING[investment]}: ${snapChartData.titleData}`;
      case 'costPerAcquisition':
      case 'roas':
        return `Overall ${investment === 'roas' ? 'ROAS' : 'CPA'}: ${snapChartData.titleData}`;
      default:
        return undefined;
    }
  }, [investment, snapChartData]);

  const snapChartFormatter = useCallback(
    value => {
      if (value === null) {
        return '--';
      }
      const isSmall = value < 0.01;
      switch (investment) {
        case 'costPerAcquisition':
          return isSmall ? '$0' : formatMoney(value);
        case 'roas':
          return isSmall ? '0' : `${value.toFixed(2)}`;
        case 'revenue':
        default:
          return isSmall ? '0%' : `${value.toFixed(2)}%`;
      }
    },
    [investment]
  );

  const xAxisTickFormatter = useCallback(
    // this isn't doing anything now, we should agree on how we display dates by grouping
    // and then move to a util and change it everywhere
    (val: string) => (dateGrouping === 'Week' ? dateToMddyy(val) : dateToMddyy(val)),
    [dateGrouping]
  );

  const [logScaleThreshold, setLogScaleThreshold] = useState<string>('1');
  const [logScaleThresholdOptions, setLogScaleThresholdOptions] = useState<
    { value: string; label: string }[]
  >([]);
  useEffect(() => {
    if (areaChartData.newD && areaChartData?.newD.length > 0) {
      // Find the maximum value across all data points
      let maxValue = 0;

      areaChartData.newD.forEach(dataPoint => {
        // Check all numeric values in the data point
        Object.values(dataPoint).forEach(value => {
          if (typeof value === 'number' && !Number.isNaN(value) && value > maxValue) {
            maxValue = value;
          }
        });
      });

      // Generate threshold options based on the max value, but go one log scale tick down
      const generateThresholdOptions = (max: number) => {
        const options: { value: string; label: string }[] = [];
        let threshold = 1;

        // Add base threshold
        options.push({ value: '1', label: '1' });

        // Find the log scale tick that contains the max value, then go one tick down
        let maxThreshold = 1;

        // Find the appropriate log scale tick for the max value
        if (max >= 1000000) {
          // Max is in the millions range, so max threshold should be 100K (one tick below 1M)
          maxThreshold = 100000;
        } else if (max >= 100000) {
          // Max is in the 100K range, so max threshold should be 10K (one tick below 100K)
          maxThreshold = 10000;
        } else if (max >= 10000) {
          // Max is in the 10K range, so max threshold should be 1K (one tick below 10K)
          maxThreshold = 1000;
        } else if (max >= 1000) {
          // Max is in the 1K range, so max threshold should be 100 (one tick below 1K)
          maxThreshold = 100;
        } else if (max >= 100) {
          // Max is in the 100 range, so max threshold should be 10 (one tick below 100)
          maxThreshold = 10;
        } else if (max >= 10) {
          // Max is in the 10 range, so max threshold should be 1 (one tick below 10)
          maxThreshold = 1;
        }

        // Generate thresholds up to the adjusted max
        while (threshold < maxThreshold) {
          if (threshold < 10) {
            threshold = 10;
          } else if (threshold < 100) {
            threshold = 100;
          } else if (threshold < 1000) {
            threshold = 1000;
          } else if (threshold < 10000) {
            threshold = 10000;
          } else if (threshold < 100000) {
            threshold = 100000;
          } else if (threshold < 1000000) {
            threshold = 1000000;
          } else {
            threshold *= 10;
          }

          if (threshold <= maxThreshold) {
            options.push({ value: threshold.toString(), label: abbreviateNumber(threshold, 0) });
          }
        }

        return options;
      };

      const thresholdOptions = generateThresholdOptions(maxValue);
      setLogScaleThresholdOptions(thresholdOptions);

      // Set default threshold to the first option
      if (thresholdOptions.length > 0) {
        setLogScaleThreshold(thresholdOptions[0].value);
      }
    }
  }, [areaChartData]);

  const [activeArea, setActiveArea] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const rightChart = useMemo(
    () =>
      investment !== 'spendAndEffectShare' ? (
        <OutcomeAndResponseChart
          areas={areaChartData.areas}
          activeArea={activeArea}
          setActiveArea={setActiveArea}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          data={areaChartData.newD}
          dateGrouping={dateGrouping}
          hardCodedColorName={HARD_CODED_COLOR_NAME}
          hardCodedColor={HARD_CODED_COLOR}
          groupedAreas={areaChartData.groupedAreas}
          tooltipFormatter={val => {
            if (investment === 'revenue' || investment === 'kpiVolume') {
              return revenueAbsoluteRelativeFormatter(val);
            }
            if (investment === 'costPerAcquisition') {
              if (val > 15 || val === 0) {
                return currencyFormatter.format(val, 0);
              }
              if (val < 15) {
                return currencyFormatter.format(val, 2);
              }
              return val;
            }
            if (investment === 'roas') {
              return val ? `${val.toFixed(2)}` : val;
            }
            return undefined;
          }}
          usePercentageYAxis={absoluteOrRelative === 'Relative'}
          xAxisDataKey='date'
          xAxisTickFormatter={val => xAxisTickFormatter(val)}
          yAxisTickFormatter={getYAxisTickFormatter(investment, outcomeVariable)}
          groupBy={groupBy}
          isResponse={investment === 'roas' || investment === 'costPerAcquisition'} // Switches to line chart instead of area chart
          logScale={logScale}
          setLogScale={setLogScale}
          logScaleThreshold={Number(logScaleThreshold)}
        />
      ) : (
        <div className='cptb' style={{ display: 'flex' }}>
          <StackedAreaChart
            areas={areaChartData.areas}
            chart1Title='Spend'
            chart2Title={`Effect (${OUTCOME_TYPE_STRING[outcomeVariable]})`}
            data1={areaChartData.newD}
            data2={areaChartData2.newD}
            dateGrouping={dateGrouping}
            groupedAreas={areaChartData.groupedAreas}
            hardCodedColorName={HARD_CODED_COLOR_NAME}
            hardCodedColor={HARD_CODED_COLOR}
            xAxisDataKey='date'
            tooltipFormatterTop={val => spendAbsoluteRelativeFormatter(val)}
            tooltipFormatterBottom={val => revenueAbsoluteRelativeFormatter(val)}
            usePercentageYAxis={absoluteOrRelative === 'Relative'}
            xAxisTickFormatter={val => xAxisTickFormatter(val)}
            yAxisTickFormatter1={val => currencyFormatter.format(val, 0)}
            yAxisTickFormatter2={val => OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0)}
          />
        </div>
      ),
    [
      investment,
      areaChartData,
      activeArea,
      activeGroup,
      dateGrouping,
      absoluteOrRelative,
      groupBy,
      logScale,
      outcomeVariable,
      areaChartData2.newD,
      revenueAbsoluteRelativeFormatter,
      xAxisTickFormatter,
      spendAbsoluteRelativeFormatter,
      logScaleThreshold
    ]
  );

  const rightChartTitle = useMemo(() => {
    const option = investmentOptions.find(item => item.value === investment);
    return option?.title;
  }, [investmentOptions, investment]);

  const excelDownloadRight = useCallback(() => {
    exportToExcel(data || [], 'Attribution_Over_Time_Data');
  }, [data]);

  const pngDownloadRight = useCallback(async () => {
    await downloadPNG('.investment-performance-container .ip-right', `${rightChartTitle}`);
  }, [rightChartTitle]);

  return (
    <div className='investment-performance-widget'>
      <WidgetContainer
        collapsible
        header={
          <div className='ip-header'>
            <span>How well did our investments do based on </span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              onChange={selection => {
                setSnapChartSortValue(DEFAULT_SORT_SELECTIONS[selection]);
                setInvestment(selection);
                if (selection === 'roas' || selection === 'costPerAcquisition') {
                  setAbsoluteOrRelative('Absolute');
                }
                setLogScale(false);
              }}
              options={investmentOptions}
              type={DropdownToggleType.PAGE_TITLE}
              value={investment}
            />
            <span>?</span>
          </div>
        }
        rightActions={
          <>
            <div className='ip-covariates-toggle'>
              <ToggleSwitch
                design={Design.BRAND}
                size={Size.LG}
                label='Covariates'
                checked={covariates}
                onChange={() => setCovariates(current => !current)}
                infoTooltipText='An independent variable that can influence the outcome of a given statistical trial, but which is not of direct interest.'
              />
            </div>
            <DatePicker
              range={dates}
              isOutsideRange={date => {
                const range = getDefaultDates(data as any[]);
                return (
                  new Date(date) < new Date(range.start) || new Date(date) > new Date(range.end)
                );
              }}
              onChange={({ start, end }) => {
                setDates({
                  start,
                  end
                });
              }}
            />
          </>
        }
        subHeader={WIDGET_SUBHEADER[investment]}
      >
        <div className='investment-performance-container'>
          <div className='ip-left'>
            {!R.isEmpty(snapChartData) && (
              <SnapshotChartWithCI
                colorOverrides={{ Baseline: Neutral400 }}
                data={snapChartData.barData}
                dropdownOptions={
                  investment === 'spendAndEffectShare'
                    ? SPEND_AND_EFFECT_SORT_OPTIONS
                    : DEFAULT_SORT_OPTIONS
                }
                dropdownValue={snapChartSortValue}
                setDropdownValue={setSnapChartSortValue}
                doubleBarChart={investment === 'spendAndEffectShare'}
                header={buildSnapshotHeader()}
                setToggleLabel={
                  investment !== 'roas' && investment !== 'costPerAcquisition'
                    ? setToggleLabel
                    : undefined
                }
                title={SNAPCHART_TITLE[investment]}
                toggleLabel={
                  investment !== 'roas' && investment !== 'costPerAcquisition' ? toggleLabel : false
                }
                toggleSwitchLabel={outcomeVariable === 'kpiVolume' ? 'Volume' : '$'}
                valueFormatter={snapChartFormatter}
                showConfidenceIntervals={investment !== 'spendAndEffectShare'}
                enableTooltip={investment !== 'spendAndEffectShare'}
                pngDownloadClassName='.investment-performance-container .ip-left'
              />
            )}
          </div>
          <div className='ip-right'>
            <ChartContainer
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={dateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={selection => setDateGrouping(selection)}
                  />
                  <div>{rightChartTitle}</div>
                </>
              }
              rightActions={
                <>
                  <ToggleSwitch
                    design={Design.NEUTRAL}
                    label='Log Scale'
                    checked={logScale}
                    onChange={() => setLogScale(current => !current)}
                    disabled={
                      absoluteOrRelative === 'Relative' ||
                      investment === 'spendAndEffectShare' ||
                      activeArea !== null ||
                      activeGroup !== null
                    }
                  />
                  {logScale && investment !== 'spendAndEffectShare' && (
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      value={logScaleThreshold}
                      options={logScaleThresholdOptions}
                      onChange={option => setLogScaleThreshold(option)}
                      label='Threshold'
                    />
                  )}
                  {(investment === 'revenue' ||
                    investment === 'kpiVolume' ||
                    investment === 'spendAndEffectShare') && (
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      value={absoluteOrRelative}
                      options={[{ value: 'Absolute' }, { value: 'Relative' }]}
                      onChange={option => {
                        if (option === 'Relative') {
                          setLogScale(false);
                        }
                        setAbsoluteOrRelative(option);
                      }}
                    />
                  )}
                  <DownloadDropdown onClickOptions={[excelDownloadRight, pngDownloadRight]} />
                </>
              }
            >
              {!R.isEmpty(areaChartData) && rightChart}
            </ChartContainer>
          </div>
        </div>
      </WidgetContainer>
    </div>
  );
};
